import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { PaymentMethodLabel, type Order, type PaymentMethod, type OrderDetail, type OrderItem, type OrderSample, type Payment } from "@probetalab/contracts";
import { get, run } from "../lib/api.js";
import { fmtDateTime, fmtDueDay, fmtPhone, fullName, money, parseMoney, tubeName } from "../lib/format.js";
import { useBranch, useSession } from "../lib/session.js";
import { ITEM_STATE_LABEL, itemTone, noSamplesText, orderChips, SAMPLE_STATE_LABEL } from "../lib/status.js";
import { useToast } from "../lib/toast.js";
import { errorMessage, useQuery } from "../lib/useQuery.js";
import { orderCredit, orderIsOpen } from "../lib/orderEdit.js";
import { EditHeaderSheet, RemoveItemSheet } from "../components/OrderEdit.js";
import { OrderPrintActions } from "../components/PrintActions.js";
import { PaySheet } from "../components/PaySheet.js";
import { LinesList, StudySearch, type Line } from "../components/StudyPicker.js";
import { pendingTubesPayload, planTubes, TubeChecklist, withKey } from "../components/TubePlan.js";
import { Alert, Button, Card, Chip, Definition, Empty, Field, IconButton, Input, Loading, Money, PageHead, Select, Sheet, TubeSwatch } from "../components/ui.js";
import { Icon } from "../components/icons.js";

/** One order: who, what, the tubes, the money, and every action that applies to its state. */
export function OrderPage() {
  const { id = "" } = useParams();
  const branch = useBranch();
  const { can } = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  const order = useQuery(() => get("order", { params: { id } }), [id]);
  const payments = useQuery(() => get("orderPayments", { params: { id } }), [id]);
  const [paying, setPaying] = useState(false);
  const [adding, setAdding] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [removing, setRemoving] = useState<OrderItem | null>(null);
  const [editingHeader, setEditingHeader] = useState(false);
  const [correcting, setCorrecting] = useState<{ payment: Payment; n: number } | null>(null);
  const [voiding, setVoiding] = useState<{ payment: Payment; n: number } | null>(null);

  if (order.loading && !order.data) return <Loading />;
  if (order.error || !order.data) return <Empty icon="warning" title="No se encontró la solicitud" hint={order.error ?? undefined} />;
  const o = order.data;
  const chips = orderChips(o);
  const live = o.items.filter((i) => i.state !== "cancelled");
  const canDeliver = o.state === "reported_final" || o.state === "reported_partial" || o.state === "closed";
  const canCapture = can("quimico") && live.some((i) => i.state === "pending" || i.state === "captured");
  const open = orderIsOpen(o);
  const credit = orderCredit(o);

  async function sampleEvent(s: OrderSample, kind: "collected" | "received" | "rejected") {
    try {
      const samples = await run<"sample.event", OrderSample[]>("sample.event", { sample_id: s.id, kind, reason: null, to_branch_id: null }, { branch_id: branch.id });
      order.set((prev) => (prev ? { ...prev, samples, has_pending_samples: samples.some((x) => x.state === "pending") } : prev));
      await order.reload();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }

  return (
    <>
      <PageHead
        kicker={`Solicitud · ${fmtDateTime(o.occurred_at)}`}
        title={
          <span className="row wrap" style={{ gap: 14 }}>
            <span className="folio">{o.folio}</span>
            <span className="row wrap" style={{ gap: 6 }}>
              {chips.map((c) => (
                <Chip key={c.label} tone={c.tone} large>
                  {c.label}
                </Chip>
              ))}
            </span>
          </span>
        }
        lede={
          <>
            <Link to={`/pacientes/${o.patient.id}`} style={{ fontWeight: 600, color: "var(--ink)" }}>
              {fullName(o.patient)}
            </Link>{" "}
            · {o.patient.age_text} · {o.doctor_display}
          </>
        }
        actions={
          <>
            <Button variant="ghost" icon="chevronLeft" onClick={() => navigate(-1)}>
              Volver
            </Button>
            {canCapture && (
              <Button variant="secondary" icon="flask" onClick={() => navigate(`/captura?folio=${encodeURIComponent(o.folio)}`)}>
                Capturar resultados
              </Button>
            )}
            {canDeliver && (
              <Button variant="primary" icon="send" onClick={() => navigate(`/entrega/${o.id}`)}>
                Entregar resultados
              </Button>
            )}
          </>
        }
      />

      <div className="split">
        <div className="stack-lg">
          <Card title="Estudios" actions={open ? <Button size="sm" variant="secondary" icon="plus" onClick={() => setAdding(true)}>Agregar estudios</Button> : undefined} pad={false}>
            <div className="table-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>Clave</th>
                    <th>Estudio</th>
                    <th>Tubo</th>
                    <th>Estado</th>
                    <th className="right">Precio</th>
                    {open && <th aria-label="Acciones" />}
                  </tr>
                </thead>
                <tbody>
                  {o.items.map((it) => (
                    <tr key={it.id} style={it.state === "cancelled" ? { opacity: 0.5 } : undefined}>
                      <td className="mono">{it.study_clave}</td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{it.study_name}</div>
                        <div className="small faint">
                          {it.area_name ?? ""}
                          {it.price_mismatch ? " · precio actualizado al registrar" : ""}
                          {it.state === "cancelled" && it.cancelled_reason ? ` · ${it.cancelled_reason}` : ""}
                          {it.state !== "cancelled" && it.due_date ? <span data-testid="item-due"> · Entrega: {fmtDueDay(it.due_date, true)}</span> : null}
                        </div>
                      </td>
                      <td className="mono small muted">{it.sample_label_code ?? "—"}</td>
                      <td>
                        <Chip tone={itemTone(it.state)}>{ITEM_STATE_LABEL[it.state]}</Chip>
                      </td>
                      <td className="right">
                        <Money cents={it.price_cents} />
                      </td>
                      {open && (
                        <td className="right">
                          {it.state !== "cancelled" && (
                            <Button size="sm" variant="ghost" icon="x" onClick={() => setRemoving(it)} aria-label={`Quitar ${it.study_clave}`}>
                              Quitar
                            </Button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card title="Muestras" pad={false}>
            {o.samples.length === 0 ? (
              <div className="list-row faint">{noSamplesText(o.origin)}</div>
            ) : (
              o.samples.map((s) => (
                <div key={s.id} className="list-row">
                  <TubeSwatch hex={s.container_colour_hex} name={tubeName(s)} />
                  <span className="mono small muted">{s.label_code}</span>
                  <span className="grow small muted">{s.study_claves.join(", ")}</span>
                  <Chip tone={s.state === "pending" ? "red" : s.state === "rejected" ? "red" : "green"} dot>
                    {SAMPLE_STATE_LABEL[s.state]}
                  </Chip>
                  {s.state === "pending" && (
                    <Button size="sm" variant="secondary" icon="dropper" onClick={() => void sampleEvent(s, "collected")}>
                      Muestra tomada
                    </Button>
                  )}
                </div>
              ))
            )}
            {o.has_pending_samples && (
              <div style={{ padding: "0 18px 16px" }}>
                <Alert tone="red">
                  <strong>Muestra pendiente.</strong> El paciente debe traerla; mientras tanto no se entregan resultados parciales.
                </Alert>
              </div>
            )}
          </Card>

          <Card title="Datos de la visita" actions={open ? <Button size="sm" variant="secondary" icon="pen" onClick={() => setEditingHeader(true)}>Editar</Button> : undefined}>
            <div className="grid-3">
              <Definition k="Médico" v={o.doctor_display} />
              <Definition k="Entrega" v={o.delivery_channels.map((c) => ({ whatsapp: "WhatsApp", print: "Impreso", email: "Correo", portal: "Portal" })[c]).join(" y ")} />
              {o.due_date && <Definition k="Fecha de entrega" v={<span data-testid="order-due">{fmtDueDay(o.due_date)}</span>} />}
              <Definition k="Ayuno" v={o.fasting_hours === null ? "No aplica" : o.fasting_hours === 0 ? "Sin ayuno" : `${o.fasting_hours} horas`} />
              <Definition k="Condición" v={o.condition ?? "Ninguna"} />
              <Definition k="Celular" v={o.patient.phone ? fmtPhone(o.patient.phone) : "—"} />
              <Definition k="Registró" v={fmtDateTime(o.created_at)} />
            </div>
            {o.clinical_notes && (
              <div className="panel" style={{ marginTop: 16 }}>
                <div className="kicker" style={{ marginBottom: 6 }}>
                  Notas clínicas
                </div>
                <p>{o.clinical_notes}</p>
              </div>
            )}
          </Card>
        </div>

        <div className="stack-lg sticky">
          <Card title="Cuenta">
            <div className="totals" style={{ paddingTop: 0 }}>
              <div className="trow">
                <span>Subtotal</span>
                <Money cents={o.subtotal_cents} />
              </div>
              {o.discount && (
                <div className="trow">
                  <span>
                    Descuento {o.discount.kind === "percent" ? `${o.discount.value} %` : ""}
                    {o.discount.reason ? ` · ${o.discount.reason}` : ""}
                  </span>
                  <span className="money">− {money(o.discount_cents)}</span>
                </div>
              )}
              <div className="trow total">
                <span>Total</span>
                <Money cents={o.total_cents} />
              </div>
              <div className="trow">
                <span>Pagado</span>
                <Money cents={o.paid_cents} />
              </div>
              {credit > 0 && (
                <div className="trow">
                  <span>Saldo a favor</span>
                  <Money cents={credit} />
                </div>
              )}
              {o.balance_cents > 0 && (
                <div className="trow due">
                  <span>Adeudo</span>
                  <Money cents={o.balance_cents} />
                </div>
              )}
            </div>
            {o.balance_cents > 0 && o.state !== "cancelled" && (
              <Button variant="primary" block icon="cash" style={{ marginTop: 16 }} onClick={() => setPaying(true)}>
                Registrar pago
              </Button>
            )}
            <div style={{ marginTop: 18 }}>
              <div className="kicker" style={{ marginBottom: 8 }}>
                Pagos
              </div>
              {payments.data?.items.length ? (
                <div className="stack" style={{ gap: 6 }}>
                  {payments.data.items.map((p: Payment, i) => (
                    <div key={p.id} className="row between small" style={p.voided_at ? { textDecoration: "line-through", opacity: 0.6 } : undefined}>
                      <span>
                        <b>Abono {i + 1}</b> · {PaymentMethodLabel[p.method]}
                        {p.reference ? ` · ${p.reference}` : ""}
                        <span className="faint"> · {fmtDateTime(p.occurred_at)} · {p.received_by_name}</span>
                        {p.voided_at && <span className="faint"> · Anulado{p.void_reason ? `: ${p.void_reason}` : ""}</span>}
                      </span>
                      <span className="row" style={{ gap: 4 }}>
                        <Money cents={p.amount_cents} />
                        {!p.voided_at && !p.cash_close_id && (
                          <IconButton icon="pen" size="sm" label={`Corregir forma de pago del abono ${i + 1}`} onClick={() => setCorrecting({ payment: p, n: i + 1 })} />
                        )}
                        {!p.voided_at && !p.cash_close_id && can("quimico") && (
                          <IconButton icon="x" size="sm" label={`Anular abono ${i + 1}`} onClick={() => setVoiding({ payment: p, n: i + 1 })} />
                        )}
                        {!p.voided_at && p.cash_close_id && <span className="faint">En corte de caja</span>}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="small faint">Sin pagos registrados.</p>
              )}
            </div>
          </Card>

          <Card title="Impresión">
            <OrderPrintActions orderId={o.id} samples={o.samples} onLabelsPrinted={() => void order.reload()} />
          </Card>

          {o.state !== "cancelled" && o.state !== "closed" && can("quimico") && (
            <Button variant="danger" icon="x" onClick={() => setCancelling(true)}>
              Cancelar solicitud
            </Button>
          )}
        </div>
      </div>

      <PaySheet
        open={paying}
        onClose={() => setPaying(false)}
        order={o}
        onPaid={(next) => {
          order.set((prev) => (prev ? { ...prev, ...next } : prev));
          void payments.reload();
          void order.reload();
        }}
      />
      <CorrectMethodSheet
        target={correcting}
        onClose={() => setCorrecting(null)}
        onDone={(next) => {
          order.set((prev) => (prev ? { ...prev, ...next } : prev));
          void payments.reload();
        }}
      />
      <VoidPaymentSheet
        target={voiding}
        onClose={() => setVoiding(null)}
        onDone={(next) => {
          order.set((prev) => (prev ? { ...prev, ...next } : prev));
          void payments.reload();
          void order.reload();
        }}
      />
      <AddItemsSheet open={adding} onClose={() => setAdding(false)} order={o} onAdded={(d) => order.set(d)} />
      <RemoveItemSheet order={o} item={removing} onClose={() => setRemoving(null)} onDone={(d) => order.set(d)} />
      <EditHeaderSheet order={o} open={editingHeader} onClose={() => setEditingHeader(false)} onDone={(d) => order.set(d)} />
      <CancelSheet
        open={cancelling}
        onClose={() => setCancelling(false)}
        order={o}
        payments={(payments.data?.items ?? []).filter((x) => !x.voided_at)}
        onDone={() => {
          void order.reload();
          void payments.reload();
        }}
      />
    </>
  );
}

const MONEY_METHODS: PaymentMethod[] = ["efectivo", "tarjeta_debito", "tarjeta_credito", "transferencia"];

/**
 * «Corregir forma de pago» (Fernanda, 2026-11-02: recorded as cash, paid with a debit card). Only
 * method and reference change; the amount, date and who received it stay. Credit to the patient
 * is not money, so it is never offered as a correction of a money payment (the server refuses it
 * as well); a credit row offers nothing to change into.
 */
export function CorrectMethodSheet({ target, onClose, onDone }: { target: { payment: Payment; n: number } | null; onClose: () => void; onDone: (o: Order) => void }) {
  const toast = useToast();
  const [method, setMethod] = useState<PaymentMethod>("efectivo");
  const [reference, setReference] = useState("");
  const [tendered, setTendered] = useState("");
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState<string | null>(null);
  const p = target?.payment ?? null;
  // Reset the fields each time the sheet opens on a payment.
  if (p && shown !== p.id) {
    setShown(p.id);
    setMethod(p.method === "efectivo" ? "tarjeta_debito" : p.method);
    setReference(p.reference ?? "");
    setTendered("");
  }
  if (!p && shown !== null) setShown(null);
  const isCredit = p?.method === "credito_paciente";
  const isCash = method === "efectivo";
  const tenderedCents = tendered.trim() === "" ? null : parseMoney(tendered);
  const tenderedOk = !isCash || tendered.trim() === "" || (tenderedCents !== null && p !== null && tenderedCents >= p.amount_cents);
  const unchanged = p !== null && method === p.method && (isCash ? tendered.trim() === "" : (reference.trim() || null) === p.reference);
  async function save() {
    if (!p) return;
    setBusy(true);
    try {
      const r = await run<"payment.correct_method", { payment: Payment; order: Order }>("payment.correct_method", {
        payment_id: p.id,
        method,
        reference: isCash ? null : reference.trim() || null,
        tendered_cents: isCash ? tenderedCents : null,
      });
      onDone(r.order);
      toast.success(`Forma de pago corregida: ${PaymentMethodLabel[method]}.`);
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Sheet
      open={p !== null}
      onClose={onClose}
      title={target ? `Corregir forma de pago · abono ${target.n}` : "Corregir forma de pago"}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={isCredit || unchanged || !tenderedOk} onClick={() => void save()}>
            Corregir
          </Button>
        </>
      }
    >
      {p && (
        <div className="stack">
          <Definition k="Monto" v={<Money cents={p.amount_cents} />} />
          <Definition k="Registrado como" v={`${PaymentMethodLabel[p.method]} · ${fmtDateTime(p.occurred_at)} · ${p.received_by_name}`} />
          {isCredit ? (
            <Alert tone="amber">Un crédito al paciente no es dinero recibido y no se cambia por un pago. Cuando el paciente pague, regístralo con «Registrar pago».</Alert>
          ) : (
            <>
              <Field label="Forma de pago correcta">
                <Select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)} aria-label="Forma de pago correcta">
                  {MONEY_METHODS.map((m) => (
                    <option key={m} value={m}>
                      {PaymentMethodLabel[m]}
                    </option>
                  ))}
                </Select>
              </Field>
              {isCash ? (
                <Field label="¿Con cuánto pagó?" optional error={tenderedOk ? null : "Es menor que el monto del abono."}>
                  <Input inputMode="decimal" value={tendered} onChange={(e) => setTendered(e.target.value)} placeholder={money(p.amount_cents)} aria-label="Con cuánto pagó" />
                </Field>
              ) : (
                <Field label="Referencia" optional hint="Número de autorización del voucher o de la transferencia.">
                  <Input value={reference} onChange={(e) => setReference(e.target.value)} aria-label="Referencia" maxLength={200} />
                </Field>
              )}
              <p className="small faint">El monto, la fecha y quién lo recibió no cambian. El cambio queda en la bitácora con tu nombre.</p>
            </>
          )}
        </div>
      )}
    </Sheet>
  );
}

function AddItemsSheet({ open, onClose, order, onAdded }: { open: boolean; onClose: () => void; order: OrderDetail; onAdded: (o: OrderDetail) => void }) {
  const branch = useBranch();
  const toast = useToast();
  const [lines, setLines] = useState<Line[]>([]);
  const [busy, setBusy] = useState(false);
  const extra = lines.reduce((s, l) => s + (l.amount_cents ?? 0), 0);
  const collectAtRegistration = useSession().me?.lab_settings.samples_collected_at_registration ?? false;
  const tubes = collectAtRegistration ? planTubes(lines) : [];
  const [pendingTubes, setPendingTubes] = useState<ReadonlySet<string>>(new Set());
  async function save() {
    setBusy(true);
    try {
      const d = await run<"order.add_items", OrderDetail>("order.add_items", { order_id: order.id, lines: lines.map((l) => ({ kind: l.kind, id: l.id, expected_amount_cents: l.amount_cents })), pending_tubes: pendingTubesPayload(tubes, pendingTubes) }, { branch_id: branch.id });
      onAdded(d);
      toast.success(`${lines.length === 1 ? "Estudio agregado" : "Estudios agregados"}. Si cabe en un tubo ya tomado, se usa el mismo.`);
      setLines([]);
      setPendingTubes(new Set());
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Agregar estudios a la solicitud"
      wide
      footer={
        <>
          <span className="grow muted">{extra > 0 ? `Se suman ${money(extra)} al total.` : ""}</span>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={!lines.length} onClick={() => void save()}>
            Agregar
          </Button>
        </>
      }
    >
      <p className="small muted">Un estudio nuevo reutiliza el tubo ya tomado cuando el recipiente y el tipo de muestra coinciden; si no, se genera una etiqueta nueva.</p>
      <StudySearch selected={lines} onPick={(it) => setLines((ls) => [...ls, it])} autoFocus priceListId={order.price_list_id} branchId={order.branch_id} />
      <LinesList lines={lines} onRemove={(l) => setLines((ls) => ls.filter((x) => !(x.kind === l.kind && x.id === l.id)))} />
      <TubeChecklist
        tubes={tubes}
        pending={pendingTubes}
        onToggle={(key, on) => setPendingTubes((p) => withKey(p, key, on))}
        note="Si el estudio cabe en un tubo que ya está en la solicitud, conserva el estado de ese tubo."
      />
    </Sheet>
  );
}

/**
 * Void one payment (Químico). The money row stays, struck through, with the reason; the order's
 * balance returns by that amount. A payment already in a cash close is not offered here.
 */
function VoidPaymentSheet({ target, onClose, onDone }: { target: { payment: Payment; n: number } | null; onClose: () => void; onDone: (o: Order) => void }) {
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState<string | null>(null);
  const p = target?.payment ?? null;
  if (p && shown !== p.id) {
    setShown(p.id);
    setReason("");
  }
  return (
    <Sheet
      open={!!target}
      onClose={onClose}
      title={target ? `Anular abono ${target.n}` : "Anular abono"}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Conservar
          </Button>
          <Button
            variant="danger"
            icon="x"
            busy={busy}
            disabled={reason.trim().length < 3}
            onClick={async () => {
              if (!p) return;
              setBusy(true);
              try {
                const r = await run<"payment.void", { payment: Payment; order: Order }>("payment.void", { payment_id: p.id, reason: reason.trim() });
                toast.success("Abono anulado.");
                onDone(r.order);
                onClose();
              } catch (e) {
                toast.error(errorMessage(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            Anular abono
          </Button>
        </>
      }
    >
      {p && (
        <>
          <Alert tone="amber">
            <Icon name="warning" style={{ display: "none" }} />
            El abono de <b>{money(p.amount_cents)}</b> ({PaymentMethodLabel[p.method]}, {fmtDateTime(p.occurred_at)}) queda tachado en la solicitud y el adeudo regresa por ese monto. No se elimina: queda en la bitácora con tu nombre y el motivo.
          </Alert>
          <Field label="Motivo">
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Por qué se anula" autoFocus />
          </Field>
        </>
      )}
    </Sheet>
  );
}

/**
 * Cancel the order. When it has active payments they are listed (amount, method, time) and voided
 * in the same server transaction with the same reason (`void_payments`); a payment already in a
 * cash close blocks the cancellation and says who can note it.
 */
function CancelSheet({ open, onClose, order, payments, onDone }: { open: boolean; onClose: () => void; order: OrderDetail; payments: Payment[]; onDone: () => void }) {
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const closed = payments.filter((x) => x.cash_close_id);
  const total = payments.reduce((n, x) => n + x.amount_cents, 0);
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={`Cancelar ${order.folio}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Conservar
          </Button>
          <Button
            variant="danger"
            icon="x"
            busy={busy}
            disabled={reason.trim().length < 3 || closed.length > 0}
            onClick={async () => {
              setBusy(true);
              try {
                await run("order.cancel", { order_id: order.id, reason: reason.trim(), void_payments: payments.length > 0 });
                toast.success(payments.length ? "Solicitud cancelada y pagos anulados." : "Solicitud cancelada.");
                onDone();
                onClose();
              } catch (e) {
                toast.error(errorMessage(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            {payments.length ? "Anular pagos y cancelar" : "Cancelar solicitud"}
          </Button>
        </>
      }
    >
      {payments.length > 0 && (
        <>
          <Alert tone="amber">
            <Icon name="warning" style={{ display: "none" }} />
            Esta solicitud tiene pagos registrados. Al cancelar se anulan estos pagos ({money(total)}) con el mismo motivo; quedan tachados en la solicitud y en la bitácora. El sistema no devuelve el dinero al paciente.
          </Alert>
          <div className="stack" style={{ gap: 6 }} data-testid="cancel-payments">
            {payments.map((x) => (
              <div key={x.id} className="row between small">
                <span>
                  {PaymentMethodLabel[x.method]}
                  <span className="faint"> · {fmtDateTime(x.occurred_at)}{x.cash_close_id ? " · En corte de caja" : ""}</span>
                </span>
                <Money cents={x.amount_cents} />
              </div>
            ))}
          </div>
          {closed.length > 0 && (
            <Alert tone="red">
              {closed.length === 1 ? "Uno de estos pagos ya está" : `${closed.length} de estos pagos ya están`} en un corte de caja cerrado y no se puede anular. Pide a un Administrador que lo anote en el corte.
            </Alert>
          )}
        </>
      )}
      {payments.length === 0 && (
        <Alert tone="amber">
          <Icon name="warning" style={{ display: "none" }} />
          La cancelación queda en la bitácora con tu nombre y el motivo.
        </Alert>
      )}
      <Field label="Motivo">
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Por qué se cancela" autoFocus />
      </Field>
    </Sheet>
  );
}
